import React, { useState, useEffect, useRef } from 'react';
import { Warehouse } from '../types';
import { useLocalization } from '../localization/LocalizationContext';

interface WarehouseAutocompleteProps {
  warehouses: Warehouse[];
  isLoading: boolean;
  onSelect: (warehouse: Warehouse) => void;
  selectedWarehouse: Warehouse | null;
  error?: string | null;
  onSearch?: (query: string) => void;
}

const WarehouseAutocomplete: React.FC<WarehouseAutocompleteProps> = ({
  warehouses,
  isLoading,
  onSelect,
  selectedWarehouse,
  error,
  onSearch,
}) => {
  const { t } = useLocalization();
  const [query, setQuery] = useState('');
  const [filteredWarehouses, setFilteredWarehouses] = useState<Warehouse[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Log props changes
  useEffect(() => {
    console.log('WarehouseAutocomplete - warehouses updated:', warehouses);
  }, [warehouses]);

  useEffect(() => {
    console.log('WarehouseAutocomplete - isLoading updated:', isLoading);
  }, [isLoading]);

  useEffect(() => {
    console.log('WarehouseAutocomplete - selectedWarehouse updated:', selectedWarehouse);
  }, [selectedWarehouse]);

  useEffect(() => {
    console.log('WarehouseAutocomplete - error updated:', error);
  }, [error]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        console.log('WarehouseAutocomplete - clicked outside, closing dropdown');
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update input value when selectedWarehouse changes
  useEffect(() => {
    if (selectedWarehouse) {
      console.log('WarehouseAutocomplete - updating query from selectedWarehouse:', selectedWarehouse.Description);
      setQuery(selectedWarehouse.Description);
    } else {
      console.log('WarehouseAutocomplete - clearing query (no selectedWarehouse)');
      setQuery('');
    }
  }, [selectedWarehouse]);

  // Filter warehouses based on query
  useEffect(() => {
    console.log('WarehouseAutocomplete - filtering warehouses with query:', query);
    console.log('WarehouseAutocomplete - warehouses available for filtering:', warehouses.length);

    if (!query.trim()) {
      console.log('WarehouseAutocomplete - empty query, showing all warehouses');
      setFilteredWarehouses(warehouses);
    } else {
      const searchTerm = query.toLowerCase();
      console.log('WarehouseAutocomplete - searching with term:', searchTerm);

      const filtered = warehouses.filter((warehouse) => {
        // Check if Description contains the query
        const descriptionMatch = warehouse.Description &&
          warehouse.Description.toLowerCase().includes(searchTerm);

        // Check if Number contains the query (for searching by warehouse number)
        const numberMatch = warehouse.Number &&
          warehouse.Number.toLowerCase().includes(searchTerm);

        // Check if ShortAddress contains the query
        const addressMatch = warehouse.ShortAddress &&
          warehouse.ShortAddress.toLowerCase().includes(searchTerm);

        return descriptionMatch || numberMatch || addressMatch;
      });

      console.log(`WarehouseAutocomplete - found ${filtered.length} matching warehouses`);
      setFilteredWarehouses(filtered);
    }
  }, [query, warehouses]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    console.log('WarehouseAutocomplete - input changed:', value);
    setQuery(value);
    setIsOpen(true);

    // Call the onSearch prop if provided
    if (onSearch) {
      console.log('WarehouseAutocomplete - calling onSearch with:', value);
      onSearch(value);
    }
  };

  const handleWarehouseSelect = (warehouse: Warehouse) => {
    console.log('WarehouseAutocomplete - warehouse selected:', warehouse);
    onSelect(warehouse);
    setIsOpen(false);
  };

  // Debug function to check if warehouses array is valid
  const debugWarehouses = () => {
    console.log('WarehouseAutocomplete - debugging warehouses array:');
    console.log('Warehouses array length:', warehouses.length);
    console.log('Warehouses array is array?', Array.isArray(warehouses));
    console.log('Filtered warehouses length:', filteredWarehouses.length);
    if (warehouses.length > 0) {
      console.log('First warehouse:', warehouses[0]);
    }
    return null;
  };

  return (
    <div className="mb-4 relative dropdown-container" ref={dropdownRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2zm0 6a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1v-2z" clipRule="evenodd" />
          </svg>
        </div>
        <input
          type="text"
          className={`shadow-sm pl-10 appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-custom-pink focus:border-custom-pink ${
            error ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={t.warehouseStep.placeholder}
          value={query}
          onChange={handleInputChange}
          onFocus={() => {
            console.log('WarehouseAutocomplete - input focused, opening dropdown');
            setIsOpen(true);
          }}
        />
        {isLoading && (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md max-h-60 overflow-auto border border-gray-200">
          {debugWarehouses()}
          {isLoading ? (
            <div className="p-4 text-center text-gray-500 flex items-center justify-center">
              <svg className="animate-spin mr-2 h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {t.warehouseStep.loading}
            </div>
          ) : filteredWarehouses && filteredWarehouses.length > 0 ? (
            <ul className="py-1">
              {filteredWarehouses.map((warehouse, index) => (
                <li
                  key={warehouse.Ref || `warehouse-${index}`}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                  onClick={() => handleWarehouseSelect(warehouse)}
                >
                  <div className="font-medium text-gray-800">
                    {warehouse.Description || `Warehouse #${warehouse.Number || 'Unknown'}`}
                  </div>
                  <div className="text-sm text-gray-500">
                    {warehouse.ShortAddress || 'Address not available'}
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="p-4 text-center text-gray-500">
              {warehouses.length > 0 ? (
                <div className="flex flex-col items-center">
                  <svg className="h-10 w-10 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p>{t.warehouseStep.noResults}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <svg className="h-10 w-10 text-gray-400 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <p>{t.warehouseStep.noWarehouses}</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WarehouseAutocomplete;